import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/Card";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  BarChart,
  ReferenceLine,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import { formatCurrency } from "@/modules/stochastic/features/Reports/SCDashboard/utils/chart";
import { TotalSalesByZipCodeDataItem } from "@/modules/stochastic/features/Reports/SCDashboard/api/fetchStochasticDashboardData/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { ChartRangeSlider } from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalHouseholdsByZipCodeChart/ChartRangeSlider";
import { ChartMetricCard } from "@/modules/stochastic/features/Reports/SCDashboard/components/ChartMetricCard/ChartMetricCard";

type TotalSalesByZipCodeChartProps = {
  chartId?: string;
  initialData: TotalSalesByZipCodeDataItem[];
};

export default function TotalSalesByZipCodeChart({
  initialData,
}: TotalSalesByZipCodeChartProps) {
  const { theme } = useTheme();

  const { minSales, maxSales } = useMemo(() => {
    if (initialData.length === 0) return { minSales: 0, maxSales: 0 };
    const sales = initialData.map((d) => d.totalSales);
    return { minSales: Math.min(...sales), maxSales: Math.max(...sales) };
  }, [initialData]);

  const [yAxisRange, setYAxisRange] = useState<[number, number]>([
    minSales,
    maxSales,
  ]);
  const [barRange, setBarRange] = useState<[number, number]>([
    1,
    initialData.length,
  ]);

  const filteredByYAxis = useMemo(
    () =>
      initialData.filter(
        (item) =>
          item.totalSales >= yAxisRange[0] && item.totalSales <= yAxisRange[1],
      ),
    [initialData, yAxisRange],
  );

  useEffect(() => {
    setBarRange([1, filteredByYAxis.length || 1]);
  }, [yAxisRange]);

  const handleYAxisChange = (value: number | [number, number]) => {
    if (Array.isArray(value)) {
      setYAxisRange(value);
    }
  };

  const handleBarRangeChange = (value: number | [number, number]) => {
    if (Array.isArray(value)) {
      setBarRange(value);
    }
  };

  const effectiveEnd = Math.min(barRange[1], filteredByYAxis.length);
  const displayData = filteredByYAxis.slice(barRange[0] - 1, effectiveEnd);

  if (initialData.length === 0) return null;

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-fontColor dark:text-light">
            Total Sales by ZIP Code
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
                <p>
                  Identifies where market capture is strongest and where
                  opportunity remains.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Revenue distribution across your service area
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex">
          <div className="flex items-stretch justify-start mr-4">
            <ChartRangeSlider
              className="h-full"
              max={maxSales}
              min={minSales}
              onChange={handleYAxisChange}
              orientation="vertical"
              tooltipFormatter={formatCurrency}
              tooltipLabel="Total Sales ($)"
              value={yAxisRange}
            />
          </div>

          <div className="flex-1">
            <ResponsiveContainer height={500} width="100%">
              <BarChart
                data={displayData}
                margin={{ top: 20, right: 40, left: 45, bottom: 5 }}
              >
                <CartesianGrid
                  stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
                  strokeDasharray="3 3"
                />
                <XAxis
                  angle={-90}
                  dataKey="postalCode"
                  fontSize={14}
                  height={100}
                  stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                  textAnchor="end"
                />
                <YAxis
                  stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                  tickFormatter={formatCurrency}
                />
                <ChartTooltip
                  contentStyle={{
                    backgroundColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
                    borderColor: theme === "dark" ? "#374151" : "#E5E7EB",
                    color: theme === "dark" ? "#F3F4F6" : "#1F2937",
                  }}
                  formatter={(value) => formatCurrency(value as number)}
                  itemStyle={{
                    color: theme === "dark" ? "#F3F4F6" : "#1F2937",
                  }}
                  labelFormatter={(label) => `ZIP Code: ${label}`}
                  labelStyle={{
                    color: theme === "dark" ? "#F3F4F6" : "#1F2937",
                  }}
                />
                <ReferenceLine stroke="#ccc" y={0} />
                <Bar
                  dataKey="totalSales"
                  fill={theme === "dark" ? "#3b82f6" : "#8884d8"}
                  name="Total Sales"
                />
              </BarChart>
            </ResponsiveContainer>

            <div className="mt-8 px-2">
              <ChartRangeSlider
                max={filteredByYAxis.length}
                min={1}
                onChange={handleBarRangeChange}
                orientation="horizontal"
                value={[
                  barRange[0],
                  Math.min(barRange[1], filteredByYAxis.length),
                ]}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-6 mt-6">
          <ChartMetricCard
            label="Bars Shown"
            value={`${barRange[0]} – ${effectiveEnd}`}
          />
          <ChartMetricCard label="ZIPs Shown" value={displayData.length} />
          <ChartMetricCard label="Total ZIPs" value={initialData.length} />
        </div>
      </CardContent>
    </Card>
  );
}
